import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Plus, Search, Settings2, Eye, EyeOff } from 'lucide-react'
import { useOrgCatalog, useCatalogOverrides, useCatalogGroups, usePricingMutations } from '@/hooks/usePricing'
import {
  SERVICE_TYPE_LABELS,
  UNIT_OF_MEASURE_LABELS,
  type ServiceCatalogItem,
  type ServiceCatalogGroup,
  type ServiceType,
  type UnitOfMeasure,
} from '@/types/pricing'

export function ServiceCatalogTab() {
  const { data: catalogItems, isLoading } = useOrgCatalog()
  const { data: overrides } = useCatalogOverrides()
  const { data: groups } = useCatalogGroups()
  const mutations = usePricingMutations()
  const [search, setSearch] = useState('')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [overrideItem, setOverrideItem] = useState<ServiceCatalogItem | null>(null)

  const overrideMap = new Map(overrides?.map((o) => [o.catalogItemId, o]) ?? [])
  const groupMap = new Map(groups?.map((g) => [g.id, g]) ?? [])

  const filtered = catalogItems?.filter((item) => {
    const override = overrideMap.get(item.id)
    const displayName = override?.displayName || item.name
    const q = search.toLowerCase()
    return (
      displayName.toLowerCase().includes(q) ||
      item.code.toLowerCase().includes(q) ||
      item.type.toLowerCase().includes(q)
    )
  })

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Service Catalog</CardTitle>
              <CardDescription>
                System-standard and custom services available for pricing.
                Override system items to rename, set GL codes, or deactivate.
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Custom Service
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search services..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Badge variant="secondary">{filtered?.length ?? 0} items</Badge>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>GL Code</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered?.map((item) => {
                const override = overrideMap.get(item.id)
                const isDeactivated = override && !override.isActive
                return (
                  <TableRow
                    key={item.id}
                    className={isDeactivated ? 'opacity-50' : undefined}
                  >
                    <TableCell className="font-mono text-xs">{item.code}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {override?.displayName || item.name}
                        {override?.displayName && (
                          <Badge variant="outline" className="text-xs">
                            renamed
                          </Badge>
                        )}
                        {isDeactivated && (
                          <Badge variant="destructive" className="text-xs">
                            hidden
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {SERVICE_TYPE_LABELS[item.type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {item.groupId ? groupMap.get(item.groupId)?.name ?? '—' : (
                        <span className="text-xs text-muted-foreground/60">Ungrouped</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {UNIT_OF_MEASURE_LABELS[item.unitOfMeasure]}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.isSystemItem ? 'default' : 'outline'}>
                        {item.isSystemItem ? 'System' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {override?.glCode || item.defaultGlCode || '-'}
                    </TableCell>
                    <TableCell>
                      {item.isSystemItem ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setOverrideItem(item)}
                          title="Override settings"
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setOverrideItem(item)}
                          title="Edit"
                        >
                          <Settings2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {filtered?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No services found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateServiceDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        groups={groups ?? []}
        onSubmit={(input) => {
          mutations.createCatalogItem.mutate(input, {
            onSuccess: () => setShowCreateDialog(false),
          })
        }}
        isPending={mutations.createCatalogItem.isPending}
      />

      <OverrideDialog
        item={overrideItem}
        override={overrideItem ? overrideMap.get(overrideItem.id) : undefined}
        onClose={() => setOverrideItem(null)}
        onSave={(input) => {
          mutations.saveCatalogOverride.mutate(input, {
            onSuccess: () => setOverrideItem(null),
          })
        }}
        isPending={mutations.saveCatalogOverride.isPending}
      />
    </>
  )
}

// --------------------------------------------------------------------------
// Create Service Dialog
// --------------------------------------------------------------------------

function CreateServiceDialog({
  open,
  onOpenChange,
  groups,
  onSubmit,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  groups: ServiceCatalogGroup[]
  onSubmit: (input: {
    code: string
    name: string
    description?: string
    type: string
    unitOfMeasure: string
    fuelType?: string
    glCode?: string
    groupId?: string
  }) => void
  isPending: boolean
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState<ServiceType>('service')
  const [unitOfMeasure, setUnitOfMeasure] = useState<UnitOfMeasure>('per_service')
  const [glCode, setGlCode] = useState('')
  const [groupId, setGroupId] = useState<string>('')

  // Filter groups by selected type using allowed_types
  const availableGroups = groups.filter(
    (g) => !g.allowedTypes || g.allowedTypes.length === 0 || g.allowedTypes.includes(type)
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      code: code.toLowerCase().replace(/\s+/g, '_'),
      name,
      type,
      unitOfMeasure,
      glCode: glCode || undefined,
      groupId: groupId && groupId !== 'none' ? groupId : undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Custom Service</DialogTitle>
            <DialogDescription>
              Create a service unique to your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Helicopter Wash"
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g., helicopter_wash"
                required
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Type</Label>
                <Select value={type} onValueChange={(v) => setType(v as ServiceType)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Unit of Measure</Label>
                <Select
                  value={unitOfMeasure}
                  onValueChange={(v) => setUnitOfMeasure(v as UnitOfMeasure)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(UNIT_OF_MEASURE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Group</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select group..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No group</SelectItem>
                  {availableGroups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="glCode">GL Code (optional)</Label>
              <Input
                id="glCode"
                value={glCode}
                onChange={(e) => setGlCode(e.target.value)}
                placeholder="e.g., 4100-001"
                className="font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name || !code}>
              {isPending ? 'Creating...' : 'Create Service'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// --------------------------------------------------------------------------
// Override Dialog (for system items)
// --------------------------------------------------------------------------

function OverrideDialog({
  item,
  override,
  onClose,
  onSave,
  isPending,
}: {
  item: ServiceCatalogItem | null
  override: { displayName: string | null; glCode: string | null; revenueCategory: string | null; isActive: boolean } | undefined
  onClose: () => void
  onSave: (input: {
    catalogItemId: string
    displayName?: string
    glCode?: string
    revenueCategory?: string
    isActive?: boolean
  }) => void
  isPending: boolean
}) {
  const [displayName, setDisplayName] = useState('')
  const [glCode, setGlCode] = useState('')
  const [isActive, setIsActive] = useState(true)

  // Sync state when item changes
useState(() => {
    if (item) {
      setDisplayName(override?.displayName || '')
      setGlCode(override?.glCode || '')
      setIsActive(override?.isActive ?? true)
    }
  })

  if (!item) return null

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {item.isSystemItem ? 'Override System Item' : 'Edit Service'}
          </DialogTitle>
          <DialogDescription>
            {item.isSystemItem
              ? `Customize "${item.name}" for your organization.`
              : `Edit "${item.name}".`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>Display Name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={item.name}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use the system default: "{item.name}"
            </p>
          </div>
          <div className="grid gap-2">
            <Label>GL Code</Label>
            <Input
              value={glCode}
              onChange={(e) => setGlCode(e.target.value)}
              placeholder={item.defaultGlCode || 'Not set'}
              className="font-mono"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Visibility</Label>
              <p className="text-xs text-muted-foreground mt-1">
                {isActive ? 'Visible in your catalog' : 'Hidden from your catalog'}
              </p>
            </div>
            <Button
              variant={isActive ? 'outline' : 'destructive'}
              size="sm"
              onClick={() => setIsActive(!isActive)}
            >
              {isActive ? (
                <>
                  <Eye className="mr-2 h-4 w-4" /> Active
                </>
              ) : (
                <>
                  <EyeOff className="mr-2 h-4 w-4" /> Hidden
                </>
              )}
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              onSave({
                catalogItemId: item.id,
                displayName: displayName || undefined,
                glCode: glCode || undefined,
                isActive,
              })
            }
            disabled={isPending}
          >
            {isPending ? 'Saving...' : 'Save Override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
